import { MyNaviFilterLayout } from '@/components/Campaign/mynavi/MyNaviModels/Filter/MyNaviFilterLayout'
import { useApi } from '@/components/Functional/hooks-core/useApi'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

const FONT_STYLE = { fontFamily: "'BIZ UDPGothic', sans-serif" }
const DAYS_OF_WEEK = ['日', '月', '火', '水', '木', '金', '土'] as const

function toISODate(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

type Week = (number | null)[]

function buildCalendar(year: number, month: number): Week[] {
  const daysInMonth = new Date(year, month, 0).getDate()
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay()
  const weeks: Week[] = []
  let week: Week = Array(firstDayOfWeek).fill(null)
  for (let day = 1; day <= daysInMonth; day++) {
    week.push(day)
    if (week.length === 7) {
      weeks.push(week)
      week = []
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null)
    weeks.push(week)
  }
  return weeks
}

type Props = {
  initialEventDateFrom: string | null
  initialEventDateTo: string | null
  /**
   * n校バッジの背景色。指定した場合は選択状態に関わらずこの色が使われる。
   * 未指定時は通常=グレー(#d9d9d9)・選択日=ブルー(#058bbe)で自動切替。
   * 例: "#ff6b35" / "rgb(255, 107, 53)"
   */
  calendarBadgeColor?: string
  /**
   * 「今日」として強調する日付。指定した日のセル背景を #FCBC54（オレンジ）で塗る。
   * 未指定時は今日ハイライトなし。
   */
  today?: Date
  /** Storybook / テスト用に外部からカレンダーデータを注入できる口 */
  calendarData?: Record<string, number>
  onSelect: (from: string | null, to: string | null) => void
  onClear: () => void
  onClose: () => void
}

export function FilterPeriod({
  initialEventDateFrom,
  initialEventDateTo,
  calendarBadgeColor,
  today,
  calendarData: calendarDataProp,
  onSelect,
  onClear,
  onClose,
}: Props) {
  const api = useApi()

  const now = new Date()
  const initialDate = initialEventDateFrom ? new Date(initialEventDateFrom) : (today ?? now)
  const [currentYear, setCurrentYear] = useState(initialDate.getFullYear())
  const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth() + 1)
  const [localDateFrom, setLocalDateFrom] = useState<string | null>(initialEventDateFrom)
  const [localDateTo, setLocalDateTo] = useState<string | null>(initialEventDateTo)

  const { data: countData } = useQuery({
    queryKey: ['mynavi-filter-count', 'period', localDateFrom, localDateTo],
    queryFn: () =>
      api.guest.fetchMynaviCollegesFilterCount({
        eventDateFrom: localDateFrom ?? undefined,
        eventDateTo: localDateTo ?? undefined,
      }),
    staleTime: 30 * 1000,
  })

  // GET /api/v2/mynavi/open-campuses/count で表示月の日別OC件数を取得
  const yearMonth = `${currentYear}${String(currentMonth).padStart(2, '0')}`
  const { data: openCampusCountData } = useQuery({
    queryKey: ['mynavi', 'open-campus-count', yearMonth],
    queryFn: () => api.guest.fetchMynaviOpenCampusCount(yearMonth),
    staleTime: 60_000,
    enabled: calendarDataProp === undefined,
  })

  // APIレスポンス（counts 配列）を Record<string, number> に変換
  // calendarDataProp が渡されていれば外部注入を優先（Storybook / テスト用）
  const calendarData = useMemo<Record<string, number>>(() => {
    if (calendarDataProp !== undefined) return calendarDataProp
    if (!openCampusCountData) return {}
    return Object.fromEntries(openCampusCountData.counts.map(({ date, count }) => [date, count]))
  }, [openCampusCountData, calendarDataProp])

  const handleDayClick = (day: number) => {
    const dateStr = toISODate(currentYear, currentMonth, day)

    if (localDateFrom === null || localDateTo !== null) {
      // 未選択 or 両方選択済み → 開始日をリセット
      setLocalDateFrom(dateStr)
      setLocalDateTo(null)
    } else if (dateStr === localDateFrom) {
      // 同じ日をタップ → 選択解除
      setLocalDateFrom(null)
    } else if (dateStr > localDateFrom) {
      // 開始日より後 → 終了日を設定
      setLocalDateTo(dateStr)
    } else {
      // 開始日より前 → 開始日を更新
      setLocalDateFrom(dateStr)
    }
  }

  const prevMonth = () => {
    if (currentMonth === 1) {
      setCurrentYear((y) => y - 1)
      setCurrentMonth(12)
    } else {
      setCurrentMonth((m) => m - 1)
    }
  }

  const nextMonth = () => {
    if (currentMonth === 12) {
      setCurrentYear((y) => y + 1)
      setCurrentMonth(1)
    } else {
      setCurrentMonth((m) => m + 1)
    }
  }

  const weeks = buildCalendar(currentYear, currentMonth)

  const todayStr = today ? toISODate(today.getFullYear(), today.getMonth() + 1, today.getDate()) : null

  return (
    <MyNaviFilterLayout
      title="開催期間を選択する"
      onClose={onClose}
      searchResultCount={countData?.count ?? null}
      onSelect={() => onSelect(localDateFrom, localDateTo)}
      onClear={() => {
        setLocalDateFrom(null)
        setLocalDateTo(null)
        onClear()
      }}
    >
      <div className="px-[26px] py-6">
        <p className="text-center text-[#686868] text-[14px] mb-[26px]" style={FONT_STYLE}>
          開催期間を選択してください
        </p>

        {/* 選択中の期間表示 */}
        {/*<div className="flex items-center justify-center gap-2 mb-4">*/}
        {/*  <div className="flex flex-col items-center">*/}
        {/*    <span className="text-[10px] text-[#686868]" style={FONT_STYLE}>*/}
        {/*      開始日*/}
        {/*    </span>*/}
        {/*    <span*/}
        {/*      className={`text-[14px] font-bold ${localDateFrom ? 'text-[#058bbe]' : 'text-[#c0c0c0]'}`}*/}
        {/*      style={FONT_STYLE}*/}
        {/*    >*/}
        {/*      {localDateFrom ? localDateFrom.replace(/-/g, '/') : '----/--/--'}*/}
        {/*    </span>*/}
        {/*  </div>*/}
        {/*  <span className="text-[#686868] text-[18px] pb-0.5">〜</span>*/}
        {/*  <div className="flex flex-col items-center">*/}
        {/*    <span className="text-[10px] text-[#686868]" style={FONT_STYLE}>*/}
        {/*      終了日*/}
        {/*    </span>*/}
        {/*    <span*/}
        {/*      className={`text-[14px] font-bold ${localDateTo ? 'text-[#058bbe]' : 'text-[#c0c0c0]'}`}*/}
        {/*      style={FONT_STYLE}*/}
        {/*    >*/}
        {/*      {localDateTo ? localDateTo.replace(/-/g, '/') : '----/--/--'}*/}
        {/*    </span>*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/* 月ナビゲーション */}
        <div className="flex items-center justify-between mb-[26px]">
          <button type="button" onClick={prevMonth} aria-label="前の月">
            <IconChevronLeft size={24} color="#686868" />
          </button>
          <span className="text-[#686868] text-[16px] font-bold" style={FONT_STYLE}>
            {currentYear}年{currentMonth}月
          </span>
          <button type="button" onClick={nextMonth} aria-label="次の月">
            <IconChevronRight size={24} color="#686868" />
          </button>
        </div>

        {/* カレンダーグリッド */}
        <table className="w-full border-collapse table-fixed">
          <thead>
            <tr>
              {DAYS_OF_WEEK.map((d, i) => (
                <th
                  key={d}
                  className={`text-center h-[31px] border border-[#cac9c9] ${
                    i === 0 ? 'bg-[#f9b6b6]' : i === 6 ? 'bg-[#b7caff]' : 'bg-[#d9d9d9]'
                  }`}
                  style={FONT_STYLE}
                >
                  <span className={`text-[10px] ${i === 0 ? 'text-[#e63536]' : 'text-[#686868]'}`} style={FONT_STYLE}>
                    {d}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, wi) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: カレンダー行のインデックスキーは安全
              <tr key={wi}>
                {week.map((day, di) => {
                  if (day === null) {
                    // biome-ignore lint/suspicious/noArrayIndexKey: 空セルのインデックスキーは安全
                    return <td key={di} className="border border-[#cac9c9] bg-white" />
                  }
                  const dateStr = toISODate(currentYear, currentMonth, day)
                  const isFrom = dateStr === localDateFrom
                  const isTo = dateStr === localDateTo
                  const isEndpoint = isFrom || isTo
                  const isInRange =
                    localDateFrom !== null && localDateTo !== null && dateStr > localDateFrom && dateStr < localDateTo
                  const isToday = todayStr !== null && dateStr === todayStr
                  const isPast = todayStr !== null && dateStr < todayStr
                  const isSunday = di === 0
                  const isSaturday = di === 6
                  const schoolCount = calendarData?.[dateStr]
                  // 優先順位: 選択日 > 範囲内 > 今日 > 過去 > 通常
                  const cellBg = isEndpoint
                    ? 'bg-[#eeecec]'
                    : isInRange
                      ? 'bg-[#daeef7]'
                      : isToday
                        ? 'bg-[#FCBC54]'
                        : isPast
                          ? 'bg-[#eeecec]'
                          : 'bg-white'

                  // n校バッジの背景色: calendarBadgeColor > today基準 > 選択状態基準
                  const badgeBgColor = calendarBadgeColor
                    ? calendarBadgeColor
                    : todayStr
                      ? dateStr < todayStr
                        ? '#d9d9d9'
                        : '#058bbe'
                      : isEndpoint
                        ? '#058bbe'
                        : '#d9d9d9'
                  const badgeTextColor = '#ffffff'
                  return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: カレンダー列のインデックスキーは安全
                    <td key={di} className={`border border-[#cac9c9] p-0 ${cellBg}`}>
                      <button
                        type="button"
                        onClick={() => handleDayClick(day)}
                        className="w-full h-[47px] flex flex-col items-center pt-[3px] pb-[6px]"
                      >
                        {/* 日付 */}
                        <span
                          className={`text-[10px] leading-none mb-auto ${
                            isSunday ? 'text-[#e63536]' : isSaturday ? 'text-blue-500' : 'text-[#686868]'
                          }`}
                          style={FONT_STYLE}
                        >
                          {day}
                        </span>
                        {/* n校バッジ */}
                        {schoolCount !== undefined && schoolCount > 0 && (
                          <div
                            className="w-full max-w-[40px] h-[20px] rounded-[3px] flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: badgeBgColor }}
                          >
                            <span className="text-[8px] leading-none" style={{ ...FONT_STYLE, color: badgeTextColor }}>
                              {schoolCount}校
                            </span>
                          </div>
                        )}
                      </button>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MyNaviFilterLayout>
  )
}
